/**
 * Document Upload Page - Quote-Native PDF Ingestion
 * 
 * NON-NEGOTIABLE RULES:
 * - Document is the source of truth
 * - Human verification required before data becomes active
 * - Zero guessing - unclear values require user input
 * - Permanent traceability to source document
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FileText, Upload, Check, AlertTriangle, Info, Eye, Trash2,
  RefreshCw, Clock, CheckCircle, ChevronRight, Sparkles, Database,
  Wallet, PiggyBank, TrendingUp, Receipt, BarChart3, Link2
} from 'lucide-react';
import axios from 'axios';
import GlassPanel from '../../components/GlassPanel';
import { useAuth } from '../../contexts/AuthContext';
import { AIPanel, AIExplanationBox, AIBadge } from '../../components/AIComponents';
import {
  ConfidenceBadge,
  StatusBadge,
  UploadDropzone,
  ExtractionSummaryCard,
  ExtractionReviewScreen
} from '../../components/DocumentUploadComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const financeSubNav = [
  { path: '/finance', label: 'Overview', icon: Wallet },
  { path: '/finance/pots', label: 'Money Pots', icon: PiggyBank },
  { path: '/finance/cashflow', label: 'Cashflow', icon: TrendingUp },
  { path: '/finance/invoices', label: 'Invoices', icon: Receipt },
  { path: '/finance/margin', label: 'Margin', icon: BarChart3 },
  { path: '/finance/documents', label: 'Documents', icon: FileText },
  { path: '/finance/xero', label: 'Xero', icon: Link2 },
];

export function DocumentUpload() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, orgId } = useAuth();
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [recentExtraction, setRecentExtraction] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [reviewingQuoteId, setReviewingQuoteId] = useState(null);

  // Fetch existing quotes
  useEffect(() => {
    const fetchQuotes = async () => {
      if (!token || !orgId) return;
      
      try {
        const response = await axios.get(
          `${API_URL}/api/supplier-quotes/`,
          {
            params: { org_id: orgId, page: 1, limit: 20 },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setQuotes(response.data.data || []);
      } catch (err) {
        console.error('Failed to fetch quotes:', err);
      } finally {
        setLoadingQuotes(false);
      }
    };

    fetchQuotes();
  }, [token, orgId]);

  // Handle file upload
  const handleFileSelect = async (file) => {
    setIsUploading(true);
    setUploadError(null);
    setRecentExtraction(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/supplier-quotes/upload`,
        formData,
        {
          params: { org_id: orgId },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setRecentExtraction(response.data);
      
      // Refresh quotes list
      const quotesResponse = await axios.get(
        `${API_URL}/api/supplier-quotes/`,
        {
          params: { org_id: orgId, page: 1, limit: 20 },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setQuotes(quotesResponse.data.data || []);
      
    } catch (err) {
      setUploadError(
        err.response?.data?.detail || 
        'Failed to upload document. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleReview = (quoteId) => {
    setReviewingQuoteId(quoteId);
  };

  const handleVerified = async () => {
    setReviewingQuoteId(null);
    setRecentExtraction(null);
    
    // Refresh quotes
    try {
      const response = await axios.get(
        `${API_URL}/api/supplier-quotes/`,
        {
          params: { org_id: orgId, page: 1, limit: 20 },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setQuotes(response.data.data || []);
    } catch (err) {
      console.error('Failed to refresh quotes:', err);
    }
  };

  // If reviewing a quote, show the review screen
  if (reviewingQuoteId) {
    return (
      <div className="page">
        <ExtractionReviewScreen
          quoteId={reviewingQuoteId}
          orgId={orgId}
          onVerified={handleVerified}
          onBack={() => setReviewingQuoteId(null)}
        />
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Document Upload</h1>
          <p>Upload supplier quotes for AI extraction and verification</p>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="flex gap-sm mb-xl" style={{ flexWrap: 'wrap' }}>
        {financeSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      {/* Non-Negotiable Rules Notice */}
      <div 
        data-testid="document-rules-notice"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginBottom: 'var(--space-lg)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
          <Info size={18} style={{ color: 'rgb(59, 130, 246)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
              How Document Extraction Works
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: 'var(--space-md)', 
              fontSize: '0.8125rem', 
              color: 'var(--text-secondary)',
              lineHeight: 1.6
            }}>
              <li><strong>Document is truth:</strong> AI extracts data verbatim - never invents or cleans up numbers.</li>
              <li><strong>Human verification:</strong> Nothing enters the system without your review and approval.</li>
              <li><strong>Full traceability:</strong> Every line item links back to its source page and text.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <GlassPanel title="Upload Supplier Quote" titleIcon={Upload} className="mb-xl">
        <UploadDropzone 
          onFileSelect={handleFileSelect}
          isUploading={isUploading}
          acceptedTypes=".pdf"
        />
        
        {uploadError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-sm) var(--space-md)',
            marginTop: 'var(--space-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}>
            <AlertTriangle size={14} style={{ color: 'var(--status-error)' }} />
            <span style={{ fontSize: '0.875rem', color: 'var(--status-error)' }}>{uploadError}</span>
          </div>
        )}
      </GlassPanel>

      {/* Recent Extraction Result */}
      {recentExtraction && (
        <div className="mb-xl">
          <h3 style={{ fontSize: '0.875rem', marginBottom: 'var(--space-md)', color: 'var(--text-secondary)' }}>
            <Sparkles size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
            Extraction Complete
          </h3>
          <ExtractionSummaryCard 
            extraction={recentExtraction}
            onReview={() => handleReview(recentExtraction.id)}
          />
        </div>
      )}

      {/* How It Works */}
      <GlassPanel title="Quote Processing Pipeline" titleIcon={Database} className="mb-xl">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-lg)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-primary-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)',
              color: 'var(--accent-primary)'
            }}>
              <Upload size={24} />
            </div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>1. Upload PDF</h4>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Drop supplier quote PDF
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-primary-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)',
              color: 'var(--accent-primary)'
            }}>
              <Sparkles size={24} />
            </div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>2. AI Extracts</h4>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Line items extracted verbatim
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-primary-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)',
              color: 'var(--accent-primary)'
            }}>
              <Eye size={24} />
            </div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>3. You Review</h4>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Verify and correct if needed
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-primary-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)',
              color: 'var(--accent-primary)'
            }}>
              <Check size={24} />
            </div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>4. Confirmed</h4>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Quote enters lineage system
            </p>
          </div>
        </div>
      </GlassPanel>

      {/* Recent Documents */}
      <GlassPanel title="Uploaded Quotes" titleIcon={FileText}>
        {loadingQuotes ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
            <RefreshCw size={20} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
          </div>
        ) : quotes.length > 0 ? (
          <div className="list-container">
            {quotes.map((quote) => (
              <div 
                key={quote.id} 
                className="list-item" 
                data-testid={`quote-list-item-${quote.id}`}
                onClick={() => handleReview(quote.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="list-item-content">
                  <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    <FileText size={16} style={{ color: 'var(--accent-primary)' }} />
                    {quote.supplier_name || 'Unknown Supplier'}
                    <ConfidenceBadge confidence={quote.extraction_confidence} showLabel={false} />
                  </div>
                  <div className="list-item-subtitle">
                    {quote.source_document_name} • {quote.line_items_count || 0} items
                  </div>
                </div>
                <div className="list-item-meta" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                  <StatusBadge status={quote.status} />
                  <span style={{ fontWeight: 600 }}>
                    £{(quote.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </span>
                  <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 'var(--space-3xl)', color: 'var(--text-muted)' }}>
            <FileText size={48} style={{ marginBottom: 'var(--space-md)', opacity: 0.5 }} />
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>No documents uploaded</h3>
            <p style={{ fontSize: '0.875rem' }}>Upload your first supplier quote PDF to get started</p>
          </div>
        )}
      </GlassPanel>

      <AIExplanationBox className="mt-xl">
        Uploaded documents are processed by AI to extract line items and prices. 
        All extracted data requires your verification before becoming active. 
        The AI never invents, infers, or cleans up values - what is in the document is what you see.
      </AIExplanationBox>
    </div>
  );
}

export default DocumentUpload;
